import BelleJS from 'belle-js'
import {
  accessToken,
  refreshToken
} from '../tokens'
import { InternalServerError } from 'utils/errorTypes'
import {
  ableToReattemptRequest,
  attemptRequestAfterRefresh,
  logOut,
  handleThirdPartyError,
  handleGeneralError
} from './apiErrorHandler'
import {
  CLIENT_ACCOUNT_PATH,
  CLIENT_TEAM_MEMBERS_PATH,
  CLIENT_INVESTMENT_PLANS_PATH,
  PORTFOLIO_HOLDINGS_PATH,
  PORTFOLIO_DETAILS_PATH,
  PORTFOLIO_ACCOUNTS_PATH,
  PORTFOLIO_CLASS_ALLOCS_PATH,
  DOCUMENTS_PATH,
  NOTIFICATIONS_PATH,
  UPDATE_USER_DISMISSED_PATH
} from './apiPaths'

export const { traveller } = BelleJS

const clientApiBaseUrl = '/v1/client'

export const apiHost = `${process.env.REACT_APP_API_HOST}`

const renewSession = (response) => {
  if (window.clientSessionHandler != null) {
    window.clientSessionHandler.renewSession()
  }

  return response
}

const handleApiErrorResponse = (endpoint, method, options, error) => {
  if (error && error.response) {
    const { response: { status } } = error
    if (status === 401) {
      const refreshCallbacks = { revokeAuthTokens, retrieveNewTokens, travellerApiRequest }

      try {
        return ableToReattemptRequest() ?
          attemptRequestAfterRefresh(endpoint, method, options, refreshCallbacks) :
          logOut(revokeAuthTokens)
      } catch (e) {
        throw e
      }
    } else {
      throw error
    }
  }
  throw error
}

const handleThirdPartyErrorResponse = (endpoint, method, options, error) => {
  if (error && error.response) {
    const { response: { status } } = error
    if (status === 503) {
      return handleThirdPartyError(endpoint, error)
    } else {
      throw new InternalServerError()
    }
  }
  throw error
}

export const travellerApiRequest = (
  endpoint,
  method,
  options = {},
  customHeaders = {},
  redirectGeneralErrorPage = true
) => {
  const token = accessToken()
  const headers = {
    Authorization: `Bearer ${token}`,
    ...customHeaders
  }

  const allOptions = { ...options, headers }
  const response = traveller(`${apiHost}${endpoint}`, method, false, allOptions)
    .then(renewSession)
    .catch(e => handleApiErrorResponse(endpoint, method, options, e))
    .catch(e => handleThirdPartyErrorResponse(endpoint, method, options, e))
    .catch(e => handleGeneralError(e, redirectGeneralErrorPage))
  return response
}

export const apiRequest = (
  endpoint,
  method,
  options = {},
  customHeaders = {},
  redirectGeneralErrorPage = true
) => (
  travellerApiRequest(`${clientApiBaseUrl}${endpoint}`, method, options, customHeaders, redirectGeneralErrorPage)
)

/* Login and Logout */
export const retrieveAccessToken = (code) => {
  const options = {
    body: JSON.stringify({
      grant_type: 'authorization_code',
      code
    })
  }
  return travellerApiRequest('/auth/token', 'POST', options)
}

export const retrieveNewTokens = async () => {
  const options = {
    body: JSON.stringify({
      grant_type: 'refresh_token',
      refresh_token: refreshToken()
    })
  }

  return travellerApiRequest('/auth/token', 'POST', options)
}

export const revokeAuthTokens = () => travellerApiRequest('/auth/token/revoke', 'DELETE')

export const verifyUser = () => apiRequest('/verify_user', 'GET')

/* Overview api requests */

export const getUser = () => apiRequest(CLIENT_ACCOUNT_PATH, 'GET')

export const getTeamMembers = () => apiRequest(CLIENT_TEAM_MEMBERS_PATH, 'GET')

export const getInvestmentPlans = () => apiRequest(CLIENT_INVESTMENT_PLANS_PATH, 'GET')

export const getNotifications = () => apiRequest(NOTIFICATIONS_PATH, 'GET')

export const updateNotificationUserDismissed = (notificationId, bodyRequest) => {
  const options = {
    body: JSON.stringify(bodyRequest)
  }

  const headers = {
    'Content-Type': 'application/json'
  }

  const userDismissedPath = UPDATE_USER_DISMISSED_PATH.replace(':notificationId', notificationId)

  return apiRequest(userDismissedPath, 'PUT', { ...options }, { ...headers })
}


export const getPortfolio = (portfolioId) => {
  const portfolioPath = PORTFOLIO_DETAILS_PATH.replace(':portfolioId', portfolioId)
  return travellerApiRequest(portfolioPath, 'GET')
}

export const getFinancialAccounts = (portfolioId) => {
  const portfolioPath = PORTFOLIO_ACCOUNTS_PATH.replace(':portfolioId', portfolioId)
  return travellerApiRequest(portfolioPath, 'GET')
}

export const getPortfolioClassAllocations = (portfolioId) => {
  const portfolioClassAllocPath = PORTFOLIO_CLASS_ALLOCS_PATH.replace(':portfolioId', portfolioId)
  return travellerApiRequest(portfolioClassAllocPath, 'GET')
}

export const getPortfolioHoldings = (portfolioId) => {
  const portfolioHoldingsPath = PORTFOLIO_HOLDINGS_PATH.replace(':portfolioId', portfolioId)
  return travellerApiRequest(portfolioHoldingsPath, 'GET')
}

export const uploadDocument = (file) => {
  const formData = new FormData()
  formData.append('file', file, file.name)
  const options = {
    body: formData
  }
  return apiRequest('/documents', 'POST', options, {}, false)
}

export const getDocuments = () => travellerApiRequest(DOCUMENTS_PATH, 'GET')

export default travellerApiRequest



// WEBPACK FOOTER //
// ./src/utils/Client/apiRequest.js